import React, { useEffect, useState } from 'react';
import { Alert, Table as AntdTable } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { Title } from 'ui';
import { Switch } from 'components/Form';
import Spinner from 'components/Spinner';
import { useT } from 'hooks/Locale/useT';
import useQueryApiClient from 'utils/useApiClient';
import { SupportSettingsOwnerType, UserValidationRule, UserValidationType } from '../../types';

interface OwnerValidationRulesProps {
    validationType: UserValidationType;
    type: Exclude<SupportSettingsOwnerType, 'default'>;
    selectedResellerId: number;
    selectedCustomerId: number;
}

// RESELLER / CUSTOMER tab of user_validations (CSV-1957). Read-only for a customer or partner
// admin: they see which RESERVED validations name them as an owner. RESERVED rules are created
// and managed only from the GLOBAL tab by a platform admin. NON_RESERVABLE never appears here -
// it is a platform-level guard.
const OwnerValidationRules: React.FC<OwnerValidationRulesProps> = ({
    validationType,
    type,
    selectedResellerId,
    selectedCustomerId,
}) => {
    const t = useT();
    const [rules, setRules] = useState<UserValidationRule[]>([]);

    const ownerId = selectedCustomerId ? selectedCustomerId : selectedResellerId;
    const scopeUrl = `api/v1/user-validations/${validationType}/${type}/${ownerId}`;

    const { fetch, isFetching } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: scopeUrl,
        },
        onSuccess: (response) => setRules(response?.data ?? []),
    });

    useEffect(() => {
        if (ownerId) {
            fetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validationType, selectedResellerId, selectedCustomerId]);

    const reservedColumns: ColumnsType<UserValidationRule> = [
        { title: t('system.environment.user_validation_value'), dataIndex: 'value', key: 'value' },
        { title: t('general.description'), dataIndex: 'description', key: 'description' },
        {
            title: t('general.status'),
            key: 'isActive',
            render: (_text: unknown, record: UserValidationRule) => (
                <Switch isFormItem={false} disabled checked={record.isActive} />
            ),
        },
    ];

    if (!ownerId) {
        return (
            <Alert
                message={t(`system.environment.please_select_a_${type === 'organization' ? 'reseller' : 'customer'}`)}
                type="info"
                showIcon
            />
        );
    }

    return (
        <Spinner spinning={isFetching}>
            <Title title="system.environment.user_validation_reserved" />
            <Alert
                className="mb-3"
                message={t('system.environment.user_validation_reserved_read_only')}
                type="info"
                showIcon
            />
            <AntdTable columns={reservedColumns} dataSource={rules} rowKey="id" size="small" pagination={false} />
        </Spinner>
    );
};

export default OwnerValidationRules;
